import genericAvatar from "assets/images/generic_avatar.jpg";
import { ErrorModal, PhotoUploader } from "components";
import { getDownloadURL } from "firebase/storage";
import { CuratedOrganization, emptyPartnerSchool } from "models";
import React, { useState } from "react";
import { Button, Col, Form, Modal } from "react-bootstrap";
import Img from "react-cool-img";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { addPartnerSchoolStory, updatePartnerSchoolStory, uploadProfilePhoto } from "services";

interface AddEditPartnerSchoolModalProps {
  /** Whether the modal should be shown. */
  show: boolean;

  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  /** Function to be called when canceling or closing the modal. */
  // onCancel: () => void;
  /** The alum to edit, or undefined if creating a new alum. */
  currentPartnerSchool: CuratedOrganization | undefined;
}

/** Modal for adding/editing curated alumni. */
export function AddEditPartnerSchoolModal({
  show,
  setShow,
  currentPartnerSchool,
}: AddEditPartnerSchoolModalProps): JSX.Element {
  const [editedPartnerSchool, setEditedPartnerSchool] = useState<CuratedOrganization>(
    currentPartnerSchool ? { ...currentPartnerSchool } : { ...emptyPartnerSchool },
  );
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | undefined>(undefined);
  const [isReplacingPhoto, setIsReplacingPhoto] = useState(false);
  const isNew = currentPartnerSchool === undefined;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [coordinates, setCoordinates] = React.useState({
    lat: 32.779167,
    lng: -96.808891,
  });
  editedPartnerSchool.longitude = coordinates.lng;
  editedPartnerSchool.latitude = coordinates.lat;
  editedPartnerSchool.type = "partner-school";

  /**
   * A function that will return the address/location of the current partner school, or
   * an empty string if this is a new partner school
   * @returns The location of the current (unedited) partner school, or an empty string if it is a new partner school
   */
  function setPartnerSchoolLocation() {
    if (currentPartnerSchool) {
      return currentPartnerSchool.location;
    }
    return "";
  }
  const [address, setAddress] = useState(setPartnerSchoolLocation());
  editedPartnerSchool.location = address;

  const handleSelect = async (mapvalue: string) => {
    const results = await geocodeByAddress(mapvalue);
    const latLng = await getLatLng(results[0]);
    setAddress(mapvalue);
    setCoordinates(latLng);
  };

  const handleClose = () => {
    setShow(false);
  };

  /**
   * This method will handle the submit function
   * First checking if the admin has submitted all the required fields
   * Then upload the photo to firebase storage and upload photo url to firestore with the profile
   * Finally upload the profile to firestore
   */
  async function submitPartnerSchool(event: {
    currentTarget: any;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      event.preventDefault(); // prevents the page from reloading immediately on submit

      if (uploadedPhoto !== undefined) {
        const uploadTask = uploadProfilePhoto(uploadedPhoto, editedPartnerSchool);
        await uploadTask;
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        editedPartnerSchool.photoUrl = url;
      }

      const submitFn: () => Promise<unknown> = isNew
        ? () => addPartnerSchoolStory(editedPartnerSchool)
        : () => updatePartnerSchoolStory(editedPartnerSchool.id, editedPartnerSchool);

      submitFn()
        .catch(err => setError(err))
        .finally(() => {
          // history.push("map");
          // history.push("map-view-admin");
          window.location.reload(); // this is not the best but my window was reloading so hard with historuy.push
        });
      setIsSubmitting(true);
    }
    setValidated(true);
  }

  return (
    <>
      <Modal show={show} size="lg" className="add-edit-alumni-modal">
        <Form noValidate validated={validated} onSubmit={submitPartnerSchool}>
          <Modal.Header>
            <Modal.Title>{isNew ? "Add New Partner School" : "Edit Partner School"}</Modal.Title>
          </Modal.Header>
          {/* <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}> */}

          <Modal.Body>
            <Form.Group>
              <strong>Basic Info</strong>
            </Form.Group>
            <Form.Row>
              <Col>
                <PartnerSchoolFormGroup
                  attribute="organization"
                  label="Partnership Name"
                  placeholder="e.g. Hudsonville Partnership"
                  onChange={e =>
                    setEditedPartnerSchool({
                      ...editedPartnerSchool,
                      organization: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedPartnerSchool.organization || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid Partnership Name.
                </Form.Control.Feedback>
              </Col>
              <Col>
                <PartnerSchoolFormGroup
                  attribute="schoolName"
                  label="Partner School Name"
                  placeholder="e.g. Hudsonville Christian School"
                  onChange={e =>
                    setEditedPartnerSchool({
                      ...editedPartnerSchool,
                      schoolName: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedPartnerSchool.schoolName || ""}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid Partner School Name.
                </Form.Control.Feedback>
              </Col>
            </Form.Row>

            <Form.Row>
              <Col>
                <Form.Label>
                  Partner School Address
                  <span className="text-danger"> *</span>
                </Form.Label>

                <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
                  {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                      <Form.Control {...getInputProps({ placeholder: "Type address" })} required />
                      <div>
                        {loading ? <div>...loading</div> : null}

                        {suggestions.map(suggestion => {
                          const style = {
                            backgroundColor: suggestion.active ? "#41b6e6" : "#fff",
                          };

                          return (
                            <div {...getSuggestionItemProps(suggestion, { style })}>
                              {suggestion.description}
                            </div>
                          );
                        })}
                      </div>
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid School Name/Location.
                      </Form.Control.Feedback>
                    </div>
                  )}
                </PlacesAutocomplete>
              </Col>
              <Col>
                <PartnerSchoolFormGroup
                  type="text"
                  attribute="date"
                  label="Partner Since"
                  placeholder={`e.g. May ${new Date().getFullYear()}`}
                  onChange={e =>
                    setEditedPartnerSchool({
                      ...editedPartnerSchool,
                      partnerSince: e.target.value.trim(),
                    })
                  }
                  defaultValue={editedPartnerSchool.partnerSince || ""}
                />
              </Col>
            </Form.Row>

            <Form.Group>
              <Form.Label>Advertisement Image</Form.Label>
              {!!editedPartnerSchool.photoUrl && !isReplacingPhoto ? (
                <Form.Row>
                  <Col xs={2}>
                    <Img
                      src={editedPartnerSchool.photoUrl || ""}
                      placeholder={genericAvatar}
                      alt={`${editedPartnerSchool.description}`}
                      width="100%"
                      loading="lazy"
                      className="img-circle"
                    />
                  </Col>
                  <Col className="d-flex align-items-center">
                    <Button variant="outline-secondary" onClick={() => setIsReplacingPhoto(true)}>
                      Replace
                    </Button>
                  </Col>
                </Form.Row>
              ) : (
                <PhotoUploader onDrop={(photos: File[]) => setUploadedPhoto(photos[0])} />
              )}
            </Form.Group>

            <PartnerSchoolFormGroup
              as="textarea"
              rows={6}
              attribute="description"
              label="Description"
              placeholder="Enter some interesting information this partner school"
              onChange={e =>
                setEditedPartnerSchool({
                  ...editedPartnerSchool,
                  description: e.target.value.trim(),
                })
              }
              defaultValue={editedPartnerSchool.description}
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="outline-primary" type="submit" disabled={isSubmitting}>
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ErrorModal error={error} />
    </>
  );
}

interface PartnerSchoolFormGroupProps {
  attribute: keyof CuratedOrganization;
  type?: "text" | "number";
  as?: "textarea";
  label: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
  defaultValue: string;
  required?: boolean;
  rows?: number;
}

function PartnerSchoolFormGroup({
  attribute,
  label,
  placeholder,
  onChange,
  defaultValue,
  type,
  as,
  required,
  rows,
}: PartnerSchoolFormGroupProps) {
  return (
    <Form.Group controlId={attribute}>
      <Form.Label>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>
      <Form.Control
        placeholder={placeholder}
        onChange={onChange}
        type={type || "text"}
        defaultValue={defaultValue}
        as={as}
        required={required}
        rows={rows}
      />
    </Form.Group>
  );
}
